/**
 * Dados de exemplo realistas para preencher os formulários da app (usado apenas
 * pelo botão "Preencher com exemplo" visível a administradores).
 * Baseados em casos-tipo do Manual do Cuidador (Coimbra) — não são casos reais.
 */

export const SAMPLE_QI = {
  contact: {
    name: "Ana Sofia Ferreira",
    phone: "912345678",
    email: "ana.ferreira@example.pt",
    relationship: "Filha",
    source: "Telefone",
    language: "pt",
    preferred_channel: "telefone",
    best_time: "tarde",
  },
  cared_person: {
    name: "Manuel Ferreira",
    age: 82,
    sex: "M",
  },
  cared_person_2: {
    name: "Rosa Ferreira",
    age: 79,
    sex: "F",
  },
  location: {
    postal_code: "1400-123",
    municipality: "Lisboa",
    district: "Lisboa",
    address: "Rua das Amendoeiras, 42 · 3ºDto",
  },
  service: {
    service_types: ["Serviço Continuado", "Externa"],
    people_count: 2,
    urgency: "1_semana",
    has_current_caregiver: false,
    start_date: "",
    schedule: "09h-18h",
    days_per_week: 5,
    needs: ["Higiene", "Alimentação", "Medicação", "Companhia"],
    notes: "Casal idoso a viver junto. Filha vive no Porto e visita ao fim-de-semana. Pai com Alzheimer moderado, mãe com mobilidade reduzida após AVC.",
  },
};

export const SAMPLE_CLINICAL_STATE_B1 = {
  diagnoses: "Doença de Alzheimer estádio moderado (GDS-5), HTA controlada, hipercolesterolemia.",
  pathologies: "Dor lombar crónica ocasional. Obstipação frequente. Sem feridas.",
  mobility: "Anda com apoio de andarilho em casa. Recusa sair à rua há 6 meses. Faz transferências com supervisão.",
  autonomy: "AVDs: banho e vestir com ajuda parcial; alimentação autónomo; WC com supervisão nocturna.",
  cognitive_state: "Desorientado no tempo, mantém orientação parcial no espaço. Reconhece familiares próximos. Episódios de agitação ao entardecer (sundowning).",
  fall_risk: "2 quedas nos últimos 6 meses (sem fratura). Tapetes soltos no corredor. Usa andarilho — apto para bengala nocturna.",
  continence: "Continência urinária diurna preservada. Incontinência nocturna esporádica — usa fralda de noite.",
  nutrition: "Come sozinho, dieta normal. Perda de 3kg em 3 meses. Filha refere apetite reduzido.",
  medication: "8 fármacos/dia (polimedicação): donepezilo, memantina, sinvastatina, losartan, omeprazol, paracetamol SOS, laxante, multivitamina. Toma sob supervisão da mulher.",
  sleep: "Dorme 6h fragmentadas. Sesta de 90min à tarde. Sem indutor. Deambulação nocturna esporádica.",
  communication: "Fala fluente com pausas. Défice auditivo ligeiro (recusa aparelho). Compreende ordens simples.",
  equipment: "Andarilho, cadeira sanitária, elevador do prédio funciona. Cama normal (sem articulação).",
  home_environment: "T3 no 3º andar com elevador. WC sem barras de apoio. Boa iluminação. Cozinha com fogão a gás — risco.",
  family_network: "Esposa (cuidadora principal, 79 anos, exausta). Filha em Lisboa (visitas semanais), filho no Porto. Sem apoio remunerado atual.",
  family_goals: "Manter o pai em casa evitando institucionalização. Aliviar a mãe. Garantir segurança à noite.",
  identified_needs: "12h/dia de apoio externo (09h-21h), 5 dias/semana. Reforço em higiene, medicação e supervisão nocturna alternada.",
};

export const SAMPLE_CLINICAL_STATE_B2 = {
  diagnoses: "Sequelas de AVC isquémico (esq. 2023), HTA, diabetes tipo 2 controlada.",
  pathologies: "Hemiparesia à direita ligeira. Fadiga marcada ao final da tarde.",
  mobility: "Anda com bengala em curtas distâncias. Sobe escadas com apoio. Faz transferências autónoma.",
  autonomy: "AVDs: banho com ajuda parcial (equilíbrio); vestir autónoma; alimentação autónoma; WC autónoma.",
  cognitive_state: "Orientada no tempo/espaço/pessoa. Sem défices de memória significativos. MMSE ~27/30.",
  fall_risk: "Sem quedas nos últimos 12 meses. Usa calçado adequado. Necessita supervisão em pisos molhados.",
  continence: "Continente urinária e fecal.",
  nutrition: "Come sozinha, dieta diabética. Peso estável. Prepara refeições ligeiras.",
  medication: "4 fármacos: metformina, losartan, AAS 100mg, atorvastatina. Toma autonomamente com pill-box semanal.",
  sleep: "Dorme 7-8h contínuas. Sem alterações.",
  communication: "Comunicação preservada. Sem défices sensoriais.",
  equipment: "Bengala. Barra apoio no WC (instalada em 2023).",
  home_environment: "Mesma habitação do marido.",
  family_network: "Marido (dependente por Alzheimer — v. Beneficiário 1). Filha e filho como B1.",
  family_goals: "Aliviar a sobrecarga de ser cuidadora informal do marido. Manter a sua qualidade de vida e ligações sociais.",
  identified_needs: "Apoio partilhado com marido (mesma Ajudante Familiar). Necessita companhia nas tardes e alívio em tarefas domésticas.",
};

export const SAMPLE_CARE_PLAN = {
  objectives: [
    "Manter autonomia funcional preservada e prevenir quedas",
    "Assegurar adesão à medicação e vigilância clínica diária",
    "Aliviar a sobrecarga do cuidador informal (esposa)",
    "Estimular cognitivamente e minimizar sundowning ao entardecer",
  ],
  priorities: [
    "Segurança nocturna (deambulação)",
    "Higiene e conforto pessoal",
    "Gestão farmacológica com pill-box",
  ],
  risks: [
    "Novo episódio de queda com fratura",
    "Sobrecarga do cuidador informal (síndrome do cuidador)",
    "Progressão do declínio cognitivo",
    "Interações medicamentosas por polimedicação",
  ],
  recommendations: [
    "Ajudante Familiar com experiência em Alzheimer e demências",
    "Instalação de barras de apoio no WC principal e nocturno",
    "Rotina estável com sinais visuais (relógio grande, calendário diário)",
    "Reavaliação clínica trimestral com geriatra",
  ],
  caregiver_profile: "Ajudante Familiar com formação em demências (mínimo 6 meses de experiência com Alzheimer), sensibilidade humana, paciência para sundowning, capacidade de comunicação clara e não-verbal, disponibilidade das 09h-18h em regime continuado.",
  interventions: [
    { objective: "Higiene e conforto", intervention: "Banho assistido de manhã, cuidados perineais e vestir. Registo diário no caderno de bordo.", frequency: "Diária, 09h-10h" },
    { objective: "Medicação supervisionada", intervention: "Preparação de pill-box semanal com esposa. Administração vigiada nas 3 tomas.", frequency: "3x/dia" },
    { objective: "Estimulação cognitiva", intervention: "Passeios curtos guiados, jogos de memória (revistas antigas, música da década de 60).", frequency: "1h/tarde" },
    { objective: "Alívio da cuidadora informal", intervention: "Coordenação com esposa para gestão do lar e refeições. Companhia à mãe (B2) enquanto pai faz sesta.", frequency: "Diária" },
  ],
};

export const SAMPLE_DIAGNOSTIC_INTERVIEW = {
  transport_regular: "yes",
  family_helper_requirements: "Experiência em demência tipo Alzheimer. Sensibilidade e paciência. Preferencialmente feminina (pedido da família). Disponibilidade contínua 09h-18h.",
  requirements_level: "elevado",
  notes: "Casal a apoiar em regime partilhado. Filha (contacto principal) vive fora e delega decisões no gestor.",
};
